import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Placeholder } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './AccountPortfolioTable.css'; // Import custom CSS for consistent styling
import { formatBalanceDisplay } from '../utils/formatBalance';
import {
  getAccountColumnDefinitions,
  ACCOUNT_TABLE_DEFAULT_VISIBLE_COLUMNS,
  ACCOUNT_TABLE_DEFAULT_COLUMN_ORDER
} from '../utils/accountTableConfig';

const AccountPortfolioTable = ({ 
  items, 
  type, 
  showTotal = false, 
  totalData = null, 
  onEditName = null, 
  onEditDescription = null, 
  showPortfolioColumn = false,
  sortConfig: externalSortConfig = null,
  onSortChange = null,
  tableLoading = false,
  visibleColumns = null,
  columnOrder = null,
  accountEdgeMap = null
}) => {
  const [sortedItems, setSortedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: externalSortConfig?.key || 'balance',
    direction: externalSortConfig?.direction || 'desc'
  });

  const columnDefinitions = useMemo(() => getAccountColumnDefinitions(type), [type]);

  // Default column visibility if not provided by parent
  const defaultVisibleColumns = ACCOUNT_TABLE_DEFAULT_VISIBLE_COLUMNS;

  // Default column order if not provided by parent - ensure it matches the order in columnDefinitions
  const defaultColumnOrder = ACCOUNT_TABLE_DEFAULT_COLUMN_ORDER;

  // Use provided column visibility or default
  const effectiveVisibleColumns = visibleColumns || defaultVisibleColumns;
  
  // Use provided column order or default
  const effectiveColumnOrder = columnOrder || defaultColumnOrder;

  // Update internal sort config when external config changes
  useEffect(() => {
    if (externalSortConfig) {
      setSortConfig(externalSortConfig);
    }
  }, [externalSortConfig]);

  // Helper to get Edge value for an item
  const getEdgeValue = useCallback((item) => {
    // Prefer position-weighted edge if provided in accountEdgeMap
    if (accountEdgeMap && item && item.account_id && typeof accountEdgeMap[item.account_id] === 'number') {
      return accountEdgeMap[item.account_id];
    }
    // Fallback to any expectancy on the item itself (legacy/raw)
    if (typeof item.expectancy === 'number') return item.expectancy;
    return null;
  }, [accountEdgeMap]);

  // Helper function to sort items
  const sortItems = useCallback((itemsToSort, key, direction) => {
    if (!key || !itemsToSort.length) return itemsToSort;
    
    return [...itemsToSort].sort((a, b) => {
      // Special handling for Edge (%) which is computed
      if (key === 'edge') {
        const aVal = getEdgeValue(a);
        const bVal = getEdgeValue(b);
        // Handle nulls: put nulls at bottom regardless of sort direction
        const aIsNull = aVal === null || aVal === undefined;
        const bIsNull = bVal === null || bVal === undefined;
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;
        return direction === 'asc' ? (aVal - bVal) : (bVal - aVal);
      }

      // Handle null values (generic)
      if (a[key] === null || a[key] === undefined) return 1;
      if (b[key] === null || b[key] === undefined) return -1;
      
      // Handle string comparisons
      if (typeof a[key] === 'string') {
        return direction === 'asc' 
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }
      
      // Handle number comparisons
      return direction === 'asc'
        ? a[key] - b[key]
        : b[key] - a[key];
    });
  }, [getEdgeValue]);

  // Remove the console.log statements to avoid excessive logging and sort items directly
  useEffect(() => {
    // Apply sorting to the items
    const sorted = sortItems(items, sortConfig.key, sortConfig.direction);
    setSortedItems(sorted);
  }, [items, sortConfig.key, sortConfig.direction, sortItems]);

  // Request sort function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    
    // Call the parent's onSortChange if provided
    if (onSortChange) {
      onSortChange(key, direction);
    }
  };

  // Get the sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Function to format percentage values
  const formatPercentage = (value) => {
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return '0.00%';
    }
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Function to determine color class based on positive/negative value
  const getColorClass = (value) => {
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return 'text-success fw-bold'; // Show green for values close to zero
    }
    if (value > 0) return 'text-success fw-bold';
    if (value < 0) return 'text-danger fw-bold';
    return 'text-secondary';
  };

  // Define column header and ID field based on type
  const getIdField = () => {
    return type === 'portfolio' ? 'portfolio_id' : 'account_id';
  };
  
  const getLinkPath = (item) => {
    return type === 'portfolio' 
      ? `/portfolio/${item.portfolio_id}` 
      : `/account/${item.account_id}`;
  };

  // Render placeholder rows for loading state
  const renderPlaceholderRows = () => {
    // Generate 5 placeholder rows
    return Array(5).fill(0).map((_, index) => (
      <tr key={`placeholder-${index}`} className="border-bottom">
        {effectiveColumnOrder.map(colKey => {
          if (!effectiveVisibleColumns[colKey]) return null;
          
          const column = columnDefinitions[colKey];
          if (!column) return null;
          
          const isRightAligned = column.align === 'right';
          
          return (
            <td key={colKey} className="py-1" style={{ textAlign: isRightAligned ? 'right' : 'left' }}>
              <Placeholder animation="glow">
                {isRightAligned ? (
                  <Placeholder xs={5} />
                ) : (
                  <Placeholder xs={6} />
                )}
              </Placeholder>
            </td>
          );
        })}
      </tr>
    ));
  };

  // Render table header based on column configuration
  const renderTableHeader = () => {
    return (
      <tr>
        {effectiveColumnOrder.map(colKey => {
          if (!effectiveVisibleColumns[colKey]) return null;
          
          const column = columnDefinitions[colKey];
          if (!column) return null;
          
          return (
            <th
                key={colKey}
                onClick={() => requestSort(column.key)}
                style={{ cursor: 'pointer', textAlign: column.align === 'right' ? 'right' : 'left' }}
                className="py-2"
              >
                {column.label} {getSortDirectionIndicator(column.key)}
                {colKey === 'bottom_line' && (
                  <i 
                    className="bi bi-info-circle ms-1" 
                    style={{ fontSize: '0.7rem', cursor: 'help' }}
                    title="Bottom line risks calculate with current account value. If you see bigger negative risks, consider raising your stop loss to reduce bottom line risks."
                    onClick={(e) => e.stopPropagation()}
                  ></i>
                )}
                {['today_gain_amount', 'today_gain_percentage'].includes(colKey) && (
                  <small className="d-block text-muted">prev bal basis</small>
                )}
              </th>
          );
        })}
      </tr>
    );
  };

  // Render table row for an item
  const renderTableRow = (item) => {
    return (
      <tr key={item[getIdField()]}>
        {effectiveColumnOrder.map(colKey => {
          if (!effectiveVisibleColumns[colKey]) return null;

          const column = columnDefinitions[colKey];
          if (!column) return null;

          switch (colKey) {
            case 'portfolio':
              return (
                <td key={colKey} className="py-1">
                  <div className="editable-cell">
                    <Link to={getLinkPath(item)}>
                      {item.name}
                    </Link>
                    {onEditName && (
                      <Button
                        variant="link"
                        size="sm"
                        className="edit-btn ms-1 p-0"
                        onClick={() => onEditName(item)}
                      >
                        <i className="bi bi-pencil-square text-secondary"></i>
                      </Button>
                    )}
                  </div>
                </td>
              );

            case 'description':
              return (
                <td key={colKey} className="py-1">
                  <div className="editable-cell">
                    {item.description || '-'}
                    {onEditDescription && (
                      <Button
                        variant="link"
                        size="sm"
                        className="edit-btn ms-1 p-0"
                        onClick={() => onEditDescription(item)}
                      >
                        <i className="bi bi-pencil-square text-secondary"></i>
                      </Button>
                    )}
                  </div>
                </td>
              );

            case 'portfolioInfo':
              return (
                <td key={colKey} className="py-1">
                  {item.portfolioInfo || '-'}
                </td>
              );
              
            case 'balance':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(item.balance)}
                </td>
              );
              
            case 'bottom_line':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(item.bottom_line || 0)}
                </td>
              );
              
            case 'cash':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(item.cash || 0)} 
                  <small className="text-muted">
                    ({item.balance > 0 ? ((item.cash || 0) / item.balance * 100).toFixed(1) : 0}%)
                  </small>
                </td>
              );
            case 'edge': {
              const edgeVal = (accountEdgeMap && accountEdgeMap[item.account_id] != null) ? accountEdgeMap[item.account_id] : (typeof item.expectancy === 'number' ? item.expectancy : null);
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(edgeVal || 0)}>
                    {edgeVal != null ? `${edgeVal.toFixed(2)}%` : '-'}
                  </span>
                </td>
              );
            }
              
            case 'today_gain_amount':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.today_gain_amount)}>
                    {formatBalanceDisplay(item.today_gain_amount)}
                  </span>
                </td>
              );
              
            case 'today_gain_percentage':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.today_gain_percentage)}>
                    {formatPercentage(item.today_gain_percentage)}
                  </span>
                </td>
              );
              
            case 'total_gain_amount':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.total_gain_amount || 0)}>
                    {formatBalanceDisplay(item.total_gain_amount || 0)}
                  </span>
                </td>
              );
              
            case 'total_gain_percentage':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.total_gain_percentage || 0)}>
                    {formatPercentage(item.total_gain_percentage || 0)}
                  </span>
                </td>
              );
              
            case 'ytd_gain_amount':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.ytd_gain_amount || 0)}>
                    {formatBalanceDisplay(item.ytd_gain_amount || 0)}
                  </span>
                </td>
              );
              
            case 'ytd_gain_percentage':
              return (
                <td key={colKey} className="py-1" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(item.ytd_gain_percentage || 0)}>
                    {formatPercentage(item.ytd_gain_percentage || 0)}
                  </span>
                </td>
              );
              
            default:
              return <td key={colKey}></td>;
          }
        })}
      </tr>
    );
  };

  // Render total row
  const renderTotalRow = () => {
    if (!showTotal || !totalData) return null;
    
    return (
      <tr className="table-total">
        {effectiveColumnOrder.map(colKey => {
          if (!effectiveVisibleColumns[colKey]) return null;

          const column = columnDefinitions[colKey];
          if (!column) return null;

          switch (colKey) {
            case 'portfolio':
              return (
                <td key={colKey} className="py-1 fw-bold">
                  Total
                </td>
              );

            case 'portfolioInfo':
              return <td key={colKey}></td>;

            case 'description':
              return <td key={colKey}></td>;
              
            case 'balance':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(totalData.balance || 0)}
                </td>
              );
              
            case 'bottom_line':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(totalData.bottom_line || 0)}
                </td>
              );
              
            case 'cash':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  {formatBalanceDisplay(totalData.cash || 0)}
                  <small className="text-muted">
                    ({totalData.balance > 0 ? ((totalData.cash || 0) / totalData.balance * 100).toFixed(1) : 0}%)
                  </small>
                </td>
              );
              
            case 'today_gain_amount':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.today_gain_amount || 0)}>
                    {formatBalanceDisplay(totalData.today_gain_amount || 0)}
                  </span>
                </td>
              );
              
            case 'today_gain_percentage':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.today_gain_percentage || 0)}>
                    {formatPercentage(totalData.today_gain_percentage || 0)}
                  </span>
                </td>
              );
              
            case 'total_gain_amount':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.total_gain_amount || 0)}>
                    {formatBalanceDisplay(totalData.total_gain_amount || 0)}
                  </span>
                </td>
              );
              
            case 'total_gain_percentage':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.total_gain_percentage || 0)}>
                    {formatPercentage(totalData.total_gain_percentage || 0)}
                  </span>
                </td>
              );
              
            case 'ytd_gain_amount':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.ytd_gain_amount || 0)}>
                    {formatBalanceDisplay(totalData.ytd_gain_amount || 0)}
                  </span>
                </td>
              );
              
            case 'ytd_gain_percentage':
              return (
                <td key={colKey} className="py-1 fw-bold" style={{ textAlign: 'right' }}>
                  <span className={getColorClass(totalData.ytd_gain_percentage || 0)}>
                    {formatPercentage(totalData.ytd_gain_percentage || 0)}
                  </span>
                </td>
              );
              
            default:
              return <td key={colKey}></td>;
          }
        })}
      </tr>
    );
  };

  return (
    <Table 
      bordered 
      hover 
      responsive 
      size="sm" 
      className="account-portfolio-table"
      style={{ width: '100%' }}
    >
      <thead>
        {renderTableHeader()}
      </thead>
      <tbody>
        {tableLoading ? (
          renderPlaceholderRows()
        ) : sortedItems.length === 0 ? (
          <tr>
            <td colSpan={Object.values(effectiveVisibleColumns).filter(Boolean).length} className="text-center py-4">
              No items found.
            </td>
          </tr>
        ) : (
          <>
            {sortedItems.map(item => renderTableRow(item))}
            {renderTotalRow()}
          </>
        )}
      </tbody>
    </Table>
  );
};

export default AccountPortfolioTable; 